'use client';

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared';
import { SectionLabel } from './styleConstants';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import styles from './AppSection.module.css';

/* ── Data ──────────────────────────────────────────────── */

type ActiveScreen = 'SLEEP' | 'VITALS' | 'POWERPLUGS' | 'LONGEVITY';

const TAB_IDS: ActiveScreen[] = ['SLEEP', 'VITALS', 'POWERPLUGS', 'LONGEVITY'];

const TAB_VIDEO_SRC: Record<ActiveScreen, string> = {
  SLEEP: 'web_v2/ring-pro/landing-page/app-videos/sleep-v2.mp4',
  VITALS: 'web_v2/ring-pro/landing-page/app-videos/vitals-v2.mp4',
  POWERPLUGS: 'web_v2/ring-pro/landing-page/app-videos/powerplugs-v2.mp4',
  LONGEVITY: 'web_v2/ring-pro/landing-page/app-videos/ultra-age.mp4',
};

/* ── Component ─────────────────────────────────────────── */

export default function AppSection() {
  const t = useTranslations('ring-pro');
  const isMobile = useWindowSize()?.width <= 768;
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('SLEEP');
  const [pulsesDisabled, setPulsesDisabled] = useState(false);
  const [pulseCycleKey, setPulseCycleKey] = useState(0);
  const [fixedPulseIndex, setFixedPulseIndex] = useState<number | null>(null);
  const [maxItemWidth, setMaxItemWidth] = useState(0);
  const metricsBarRef = useRef<HTMLDivElement | null>(null);
  const videoRefs = useRef<Record<ActiveScreen, HTMLVideoElement | null>>({
    SLEEP: null,
    VITALS: null,
    LONGEVITY: null,
    POWERPLUGS: null,
  });

  const PULSE_MIN_DELAY_MS = 5000;
  const PULSE_MAX_DELAY_MS = 8000;

  const tabData = useMemo(
    () =>
      TAB_IDS.map((id) => ({
        id,
        label: t(`ring-pro.app.tabs.${id.toLowerCase()}`),
        videoSrc: TAB_VIDEO_SRC[id],
      })),
    [t],
  );

  const activeIndex = useMemo(
    () => tabData.findIndex((tab) => tab.id === activeScreen),
    [activeScreen, tabData],
  );

  useLayoutEffect(() => {
    const measure = () => {
      const root = metricsBarRef.current;
      if (!root) return;
      const items = root.querySelectorAll<HTMLButtonElement>(
        '[data-metric-item="true"]',
      );
      let max = 0;
      items.forEach((el) => {
        if (el.offsetWidth > max) max = el.offsetWidth;
      });
      if (max) setMaxItemWidth(max);
    };

    const timeoutId = setTimeout(measure, 0);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timeoutId);
    };
  }, []);

  useLayoutEffect(() => {
    const root = metricsBarRef.current;
    if (!root || !maxItemWidth) return;
    root.style.setProperty('--itemWidth', `${maxItemWidth}px`);
  }, [maxItemWidth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const connection = (navigator as any).connection;
    if (connection?.saveData) return;

    const urls = tabData
      .filter((t) => t.id !== activeScreen)
      .map((t) => t.videoSrc)
      .filter(Boolean)
      .map((src) =>
        getCompressedAssetUrl(src.startsWith('/') ? src : `/${src}`),
      );

    urls.forEach((url) => {
      if (document.head.querySelector(`link[rel="prefetch"][href="${url}"]`))
        return;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.crossOrigin = 'anonymous';
      link.setAttribute('fetchpriority', 'low');
      document.head.appendChild(link);
    });
  }, []);

  const handleScreenChange = (screen: ActiveScreen) => {
    setActiveScreen(screen);
    if (!pulsesDisabled) setPulsesDisabled(true);
  };

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      (Object.keys(videoRefs.current) as ActiveScreen[]).forEach((key) => {
        const el = videoRefs.current[key];
        if (!el) return;
        if (key === activeScreen) {
          try {
            el.currentTime = 0;
          } catch (_) {
            /* not seekable yet */
          }
          void el.play().catch(() => undefined);
        } else {
          el.pause();
        }
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeScreen]);

  useEffect(() => {
    const inactiveIndices = tabData
      .map((_, idx) => idx)
      .filter((idx) => idx !== activeIndex);
    if (inactiveIndices.length === 0) return;
    setFixedPulseIndex(
      inactiveIndices[Math.floor(Math.random() * inactiveIndices.length)],
    );
  }, []);

  useEffect(() => {
    if (pulsesDisabled) return;
    const delay =
      Math.floor(
        Math.random() * (PULSE_MAX_DELAY_MS - PULSE_MIN_DELAY_MS + 1),
      ) + PULSE_MIN_DELAY_MS;
    const id = window.setTimeout(() => {
      if (fixedPulseIndex === null || fixedPulseIndex === activeIndex) return;
      setPulseCycleKey((k) => k + 1);
    }, delay);
    return () => clearTimeout(id);
  }, [activeIndex, pulsesDisabled, pulseCycleKey, fixedPulseIndex]);

  const phoneImage = isMobile
    ? 'web_v2/homepage-v3/app-section/mobile-mockup-sm.png'
    : 'web_v2/homepage-v3/app-section/hand-phone-mockup.png';

  const getLinkSrc = (link: string) => {
    if (!link) return '';
    return getCompressedAssetUrl(link.startsWith('/') ? link : `/${link}`);
  };

  return (
    <section className={styles.section} id="app-section">
      <div className={styles.contentWrapper}>
        <div className={styles.textBlock}>
          <SectionLabel>{t('ring-pro.app.label')}</SectionLabel>
          <h2 className={styles.heading}>{t('ring-pro.app.heading')}</h2>
        </div>
        <div className={styles.phoneWrapper}>
          <CustomImage
            className={styles.phoneImage}
            src={phoneImage}
            height={4444}
            width={5000}
            sizes="(min-width: 992px) 600px, 100vw"
            alt={t('ring-pro.app.phoneAlt')}
          />
          <div className={styles.phoneScreen} id="ring-pro-app-panel">
            {tabData
              .filter((tab) => tab.videoSrc)
              .map((tab) => (
                <video
                  className={styles.videoLayer}
                  key={tab.id}
                  data-active={activeScreen === tab.id ? 'true' : undefined}
                  src={getLinkSrc(tab.videoSrc)}
                  preload={activeScreen === tab.id ? 'auto' : 'metadata'}
                  autoPlay={activeScreen === tab.id}
                  loop
                  muted
                  playsInline
                  aria-hidden={activeScreen !== tab.id}
                  ref={(el) => {
                    videoRefs.current[tab.id] = el;
                  }}
                />
              ))}
          </div>
          <div
            className={styles.metricsBar}
            ref={metricsBarRef}
            role="tablist"
            aria-label={t('ring-pro.app.metricsAriaLabel')}
          >
            <div
              className={styles.activeHighlight}
              style={{
                '--ah-index': activeIndex,
                '--ah-tabcount': tabData.length ?? 4,
              } as React.CSSProperties}
            />
            {tabData.map((tab, index) => {
              const showPulse =
                fixedPulseIndex === index &&
                !pulsesDisabled &&
                activeIndex !== index;
              return (
                <button
                  className={styles.metricItem}
                  key={tab.id}
                  data-metric-item="true"
                  data-active={activeIndex === index ? 'true' : undefined}
                  data-disable-pulses={pulsesDisabled ? 'true' : undefined}
                  onClick={() => handleScreenChange(tab.id)}
                  role="tab"
                  aria-selected={activeIndex === index}
                  aria-controls="ring-pro-app-panel"
                >
                  {showPulse && (
                    <div
                      className={styles.pulseRings}
                      aria-hidden="true"
                      key={`pulse-${pulseCycleKey}`}
                    >
                      <span className={styles.ring} />
                      <span className={styles.ring} />
                      <span className={styles.ring} />
                    </div>
                  )}
                  <span className={styles.label}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
